import dayjs from "dayjs";
import { useRef, DragEvent, useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { IApiErrorResponse } from "../../../../@types/ApiError";
import {
  ICustomFieldAnswer,
  ICustomFieldFormItem,
  IPatientCustomFieldValue,
} from "../../../../@types/CustomField";
import { PatientPropChange } from "../../../../@types/Patient";
import { IOperationValue, IPatchOperation } from "../../../../@types/Request";
import { ArrowRight } from "../../../../assets/icons/ArrowRight";
import { IOption } from "../../../../components/SelectDropdown";
import {
  sexOptions,
  ethnicityOptions,
} from "../../../../constants/patientDetails";
import { useSetPatientCustomFields } from "../../../../hooks/mutations/customFields";
import { useSetPatientWards } from "../../../../hooks/mutations/patients";
import { usePatientCustomFields } from "../../../../hooks/queries/customFields";
import {
  usePatientDetailsDropdownItems,
  usePatientInfo,
  usePatientMonitoringInfo,
  usePatientWards,
  useUserPreferencesData,
} from "../../../../hooks/queries/patients";
import { useModal } from "../../../../hooks/useModal";
import { useToast } from "../../../../hooks/useToast";
import { patientService } from "../../../../services/patientService";
import {
  buildCustomFieldEditPayload,
  formatCustomFieldDisplay,
  validateCustomFields,
} from "../../../../utils/customFields";
import { getShortFormattedUtcDate } from "../../../../utils/dateFormatter";
import { serverErrorMessage } from "../../../../utils/HttpError";
import {
  getEnabledOnboardingFields,
  isOnboardingFieldEnabled,
  isOnboardingFieldRequired,
  OnboardingFieldCode,
} from "../../../../utils/onboardingFields";
import { resolvePatientWardIds } from "../../../../utils/wardConfig";
import { PatientCard, PatientMenuCard } from "../PatientMenuCard";
import { cardsCofigs, getCardLabelKey } from "./cardsConfigs";
import { ConfirmChangesModal } from "./components/ConfirmChangesModal";
import {
  EditButton,
  ExpandButton,
  EditControlsButton,
  PatientLateralMenu,
  EditButtonContainer,
  EditControlsContainer,
  PatientLateralMenuCardContainer,
} from "./PatientLateralMenu.style";

// EN-5067: custom-field cards use a `custom:<definitionId>` name so they never collide with the
// built-in card names and keep a stable identity across saved card-order round-trips.
const CUSTOM_CARD_PREFIX = "custom:";

const ONBOARDING_CARD_TO_CODE: Record<string, OnboardingFieldCode> = {
  Allergies: "Allergies",
  Diagnosis: "Diagnosis",
};

type PatientLateralMenuDesktopProps = {
  open: boolean;
  onExpandClick: () => void;
  onEditingClick: (isEditing: boolean) => void;
  patientId: string | undefined;
};

export function PatientLateralMenuDesktop({
  open,
  patientId,
  onExpandClick,
  onEditingClick,
}: PatientLateralMenuDesktopProps) {
  const { t } = useTranslation();

  const menuRef = useRef<HTMLDivElement>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [patientCards, setPatientCards] = useState<PatientCard[]>([]);
  const [propsChanged, setPropsChanged] = useState<PatientPropChange[]>([]);
  const [lastPatientCards, setLastPatientCards] = useState<PatientCard[]>(
    [] as PatientCard[]
  );

  const { patient, refetchPatientInfo } = usePatientInfo({ patientId });
  const { patientMonitoringInfo } = usePatientMonitoringInfo({ patientId });
  const { openModal, closeModal } = useModal();
  const { dropdownItems } = usePatientDetailsDropdownItems({ patientId });
  // EN-4960: a patient can belong to multiple wards; show them all, primary first.
  const { patientWards, isPatientWardsSuccess, refetchPatientWards } =
    usePatientWards(patient?.id);
  // EN-5067: the patient's side-panel custom fields (values + editor metadata), shown as cards.
  const { patientCustomFields, refetchPatientCustomFields } =
    usePatientCustomFields(patient?.id);

  const { mutateAsync: setPatientWards } = useSetPatientWards();
  const { mutateAsync: setPatientCustomFields } = useSetPatientCustomFields();

  // EN-4960: the Ward card is edited inline as a multi-select. Its selection lives here (not in
  // the JSON-patch propsChanged flow) because it saves via PUT /Wards. `originalWardIdsRef` holds
  // the set captured when editing started, so we can tell whether it actually changed on Save.
  const [selectedWardIds, setSelectedWardIds] = useState<number[]>([]);
  const originalWardIdsRef = useRef<number[]>([]);

  // EN-5067: custom-field cards are edited inline (Ward pattern) and saved via PUT /CustomFields.
  // `customFieldEdits` holds only the fields touched this session; `originalCustomFieldAnswersRef`
  // snapshots the values when editing started so we can tell what actually changed.
  const [customFieldEdits, setCustomFieldEdits] = useState<
    Record<string, ICustomFieldAnswer>
  >({});
  const originalCustomFieldAnswersRef = useRef<
    Record<string, ICustomFieldAnswer>
  >({});

  const wardOptions: IOption[] = useMemo(
    () =>
      (dropdownItems?.hospitalItems ?? []).map((item, index) => ({
        label: item.itemName,
        value: item.itemId.toString(),
        key: `${item.itemId}-${index}`,
      })),
    [dropdownItems]
  );

  const wardNamesFor = (ids: number[]): string =>
    ids
      .map(
        (id) =>
          dropdownItems?.hospitalItems?.find((h) => h.itemId === id)?.itemName
      )
      .filter((name): name is string => !!name)
      .join(", ") || "-";

  const sameWardSet = (a: number[], b: number[]): boolean =>
    a.length === b.length && [...a].sort().join() === [...b].sort().join();

  const handleWardChange = (values: string[]) => {
    const ids = values
      .map((value) => parseInt(value, 10))
      .filter((id) => !Number.isNaN(id));

    setSelectedWardIds(ids);

    // Reflect the change in propsChanged so the Confirm Changes modal lists it; drop the entry
    // again if the user reverts to the original set.
    const changed = !sameWardSet(ids, originalWardIdsRef.current);
    setPropsChanged((previous) => {
      const withoutWard = previous.filter((prop) => prop.propName !== "Ward");
      if (!changed) return withoutWard;
      return [
        ...withoutWard,
        {
          propName: "Ward",
          newValue: wardNamesFor(ids),
          oldValue: wardNamesFor(originalWardIdsRef.current),
        },
      ];
    });
  };

  // EN-5067 custom-field edit helpers ---------------------------------------------------------
  const sameAnswer = (
    a: ICustomFieldAnswer,
    b: ICustomFieldAnswer
  ): boolean => {
    const av = (a.value ?? "").trim();
    const bv = (b.value ?? "").trim();
    const as = [...(a.selectedOptions ?? [])].sort().join("|");
    const bs = [...(b.selectedOptions ?? [])].sort().join("|");
    return av === bv && as === bs;
  };

  const answerDisplay = (
    field: IPatientCustomFieldValue,
    answer: ICustomFieldAnswer
  ): string =>
    formatCustomFieldDisplay({
      ...field,
      value: answer.value ?? null,
      selectedOptions: answer.selectedOptions ?? [],
    });

  // Mirror of handleWardChange for a single custom field: track the working value and reflect the
  // change in propsChanged (keyed `custom:<id>`, with the label as displayName), dropping it again
  // if the user reverts to the original value.
  const handleCustomFieldChange = (
    definitionId: string,
    answer: ICustomFieldAnswer
  ) => {
    setCustomFieldEdits((previous) => ({
      ...previous,
      [definitionId]: answer,
    }));

    const field = patientCustomFields.find(
      (item) => item.customFieldDefinitionId === definitionId
    );
    if (!field) return;

    const original = originalCustomFieldAnswersRef.current[definitionId] ?? {};
    const changed = !sameAnswer(answer, original);
    const propName = `${CUSTOM_CARD_PREFIX}${definitionId}`;

    setPropsChanged((previous) => {
      const without = previous.filter((prop) => prop.propName !== propName);
      if (!changed) return without;
      return [
        ...without,
        {
          propName,
          displayName: field.label,
          newValue: answerDisplay(field, answer),
          oldValue: answerDisplay(field, original),
        },
      ];
    });
  };
  const {
    userPreferences,
    refetch,
    isFetching: isPreferencesFetching,
  } = useUserPreferencesData();
  const { showToast } = useToast();

  const enabledOnboardingFields = useMemo(
    () => getEnabledOnboardingFields(),
    []
  );

  const isOnboardingCardVisible = (cardName: string): boolean => {
    const code = ONBOARDING_CARD_TO_CODE[cardName];
    if (!code) return true;
    return isOnboardingFieldEnabled(code, enabledOnboardingFields);
  };

  const isCardRequired = (cardName: string): boolean => {
    const code = ONBOARDING_CARD_TO_CODE[cardName];
    if (!code) return false;
    return isOnboardingFieldRequired(code, enabledOnboardingFields);
  };

  // Value -> label and back. The reverse lookups below feed the patch payload, so both
  // directions have to read the label through the same `t` or a translated panel would stop
  // resolving the value the API expects (EN-5191).
  const getLabelGender = (value?: string) => {
    const object = sexOptions.find((a) => value === a.value);
    return object && t(object.labelKey);
  };
  const getLabelEthnicity = (value?: string) => {
    const object = ethnicityOptions.find((a) => value === a.value);
    return object && t(object.labelKey);
  };

  // EN-4960: render all of the patient's ward names, falling back to the primary ward from
  // hospitalId if the ward set hasn't loaded. "Primary" is an internal concept, not surfaced.
  const wardsDescription = (() => {
    const names = resolvePatientWardIds(
      patientWards?.wardIds,
      patient.hospitalId
    )
      .map(
        (id) =>
          dropdownItems?.hospitalItems?.find((h) => h.itemId === id)?.itemName
      )
      .filter((name): name is string => !!name);

    if (!names.length) return "-";

    return names.join(", ");
  })();

  // EN-5067: one card per side-panel custom field, ordered by the field's `displayOrder` so they
  // keep the admin-configured sequence among themselves. Orders start at 100 so they append after
  // the built-in catalog. A newly-available field defaults to inactive/off — the HCP opts it in via
  // "manage fields" (like any built-in that isn't shown by default) and can then move/toggle it
  // freely; the chosen state persists in the cards-order blob. The metadata
  // (label/type/options/validation/isRequired) rides on customFieldMeta so the card renders the
  // read-only display AND the inline editor. `customCardsByName` is used by the reconcile pass to
  // re-hydrate a saved custom card.
  const customFieldCards: PatientCard[] = [...patientCustomFields]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((field, index) => ({
      order: 100 + index,
      active: false,
      name: `${CUSTOM_CARD_PREFIX}${field.customFieldDefinitionId}`,
      description: formatCustomFieldDisplay(field),
      customFieldMeta: {
        definitionId: field.customFieldDefinitionId,
        label: field.label,
        type: field.type,
        options: field.options ?? [],
        validation: field.validation ?? {},
        isRequired: field.isRequired ?? false,
      },
    }));
  const customCardsByName = new Map(
    customFieldCards.map((card) => [card.name, card])
  );

  const defaultCards = (
    [
      {
        order: 1,
        active: true,
        name: "First name",
        labelKey: getCardLabelKey("First name"),
        description: patient.user?.firstName || "-",
      },
      {
        order: 2,
        active: true,
        name: "Last name",
        labelKey: getCardLabelKey("Last name"),
        description: patient.user?.lastName || "-",
      },
      {
        order: 3,
        active: true,
        name: "DOB",
        labelKey: getCardLabelKey("DOB"),
        description: patient.birthDate
          ? getShortFormattedUtcDate(patient.birthDate)
          : "-",
      },
      {
        order: 4,
        active: true,
        name: "App version",
        labelKey: getCardLabelKey("App version"),
        description: patient?.appVersion || "-",
      },
      {
        order: 5,
        active: true,
        name: "Hospital ID",
        labelKey: getCardLabelKey("Hospital ID"),
        description: patient?.hospitalPatientId || "-",
      },
      {
        order: 6,
        active: true,
        name: "PMP ID",
        labelKey: getCardLabelKey("PMP ID"),
        description: patient?.id.toString() || "-",
      },
      {
        order: 7,
        active: true,
        name: "Email",
        labelKey: getCardLabelKey("Email"),
        description: patient?.user?.email || "-",
      },
      {
        order: 8,
        active: true,
        name: "Last Review",
        labelKey: getCardLabelKey("Last Review"),
        description: patientMonitoringInfo?.latestReview || "-",
      },
      {
        order: 9,
        active: true,
        name: "Last Reviewed By",
        labelKey: getCardLabelKey("Last Reviewed By"),
        description: patientMonitoringInfo?.latestReviewedBy || "-",
      },
      {
        order: 10,
        active: true,
        name: "Ward",
        description: wardsDescription,
      },
      {
        order: 11,
        active: true,
        name: "Last Used",
        labelKey: getCardLabelKey("Last Used"),
        description: patient.user?.lastUsed || "-",
      },
      {
        order: 12,
        active: false,
        name: "Weight (kg)",
        labelKey: getCardLabelKey("Weight (kg)"),
        description: patient.user?.weight || "-",
      },
      {
        order: 13,
        active: false,
        name: "Height (cm)",
        labelKey: getCardLabelKey("Height (cm)"),
        description: patient.user?.height || "-",
      },
      {
        order: 14,
        active: false,
        name: "Country",
        labelKey: getCardLabelKey("Country"),
        description: patient.user?.country || "-",
      },
      {
        order: 16,
        active: false,
        name: "Caregiver name",
        labelKey: getCardLabelKey("Caregiver name"),
        description: patient.user?.caregiverName || "-",
      },
      {
        order: 17,
        active: false,
        name: "Caregiver phone number",
        labelKey: getCardLabelKey("Caregiver phone number"),
        description: patient.user?.caregiverPhoneNumber || "-",
      },
      {
        order: 18,
        active: false,
        name: "Goals",
        labelKey: getCardLabelKey("Goals"),
        description: patientMonitoringInfo?.goals || "-",
      },
      {
        order: 19,
        active: false,
        name: "Phone",
        labelKey: getCardLabelKey("Phone"),
        description: patient?.user?.phoneNumber || "-",
      },
      {
        order: 20,
        active: true,
        name: "Gender",
        labelKey: getCardLabelKey("Gender"),
        description: getLabelGender(patient?.gender) || "-",
      },
      {
        order: 21,
        active: true,
        name: "Ethnicity",
        labelKey: getCardLabelKey("Ethnicity"),
        description: getLabelEthnicity(patient?.ethnicity) || "-",
      },
      {
        order: 30,
        active: false,
        name: "Allergies",
        labelKey: getCardLabelKey("Allergies"),
        description:
          patient?.allergies && patient.allergies.length > 0
            ? patient.allergies
                .map((a) => `${a.preferredTerm} - ${a.conceptId}`)
                .join("\n")
            : "-",
      },
      {
        order: 31,
        active: false,
        name: "Diagnosis",
        labelKey: getCardLabelKey("Diagnosis"),
        description:
          patient?.diagnoses && patient.diagnoses.length > 0
            ? patient.diagnoses
                .map((d) => `${d.preferredTerm} - ${d.conceptId}`)
                .join(", ")
            : "-",
      },
      // EN-5060: the condition card was removed — conditions are managed in Retool only.
    ] as PatientCard[]
  )
    // EN-5067: append the dynamic custom-field cards to the catalog.
    .concat(customFieldCards);

  const setDefaultCardsOrder = () => {
    setPatientCards(defaultCards);
  };

  const getObjectByName = (name: string) => {
    return defaultCards.find((a) => a.name === name);
  };

  useEffect(() => {
    if (!patient && !patientMonitoringInfo) return;

    // Wait for the saved card order to finish loading before building the cards. Otherwise
    // we'd paint the default order first and then reflow to the saved order once the
    // preferences arrive — the visible flicker the side nav showed on refresh.
    if (isPreferencesFetching) return;

    if (
      userPreferences &&
      userPreferences.patientDetailsCardsOrder &&
      userPreferences.patientDetailsCardsOrder !== "[]"
    ) {
      const parsedPatientCards = JSON.parse(
        userPreferences.patientDetailsCardsOrder
      ) as PatientCard[];

      const temporaryPatientCards: PatientCard[] = [];

      if (parsedPatientCards) {
        parsedPatientCards.forEach((card) => {
          const temporaryCard = { ...card };

          switch (card.name) {
            case "First name":
              temporaryCard.description = patient.user?.firstName || "-";
              break;
            case "Last name":
              temporaryCard.description = patient.user?.lastName || "-";
              break;
            case "DOB":
              temporaryCard.description = patient.birthDate
                ? getShortFormattedUtcDate(patient.birthDate)
                : "-";
              break;
            case "App version":
              temporaryCard.description = patient?.appVersion || "-";
              break;
            case "Hospital ID":
              temporaryCard.description = patient?.hospitalPatientId || "-";
              break;
            case "PMP ID":
              temporaryCard.description = patient?.id.toString() || "-";
              break;
            case "Email":
              temporaryCard.description = patient?.user?.email || "-";
              break;
            case "Last Reviewed By":
              temporaryCard.description =
                patientMonitoringInfo?.latestReviewedBy || "-";
              break;
            case "Last Review":
              temporaryCard.description =
                patientMonitoringInfo?.latestReview || "-";
              break;
            case "Ward":
              // EN-4960: show ALL of the patient's wards (primary first), not just the primary.
              temporaryCard.description = wardsDescription;
              break;
            case "Last Used":
              temporaryCard.description = patient.user?.lastUsed || "-";
              break;
            case "Weight (kg)":
              temporaryCard.description = patient.user?.weight || "-";
              break;
            case "Height (cm)":
              temporaryCard.description = patient.user?.height || "-";
              break;
            case "Country":
              temporaryCard.description = patient.user?.country || "-";
              break;
            case "Caregiver name":
              temporaryCard.description = patient.user?.caregiverName || "-";
              break;
            case "Caregiver phone number":
              temporaryCard.description =
                patient.user?.caregiverPhoneNumber || "-";
              break;
            case "Goals":
              temporaryCard.description = patientMonitoringInfo?.goals || "-";
              break;
            case "Phone":
              temporaryCard.description = patient?.user?.phoneNumber || "-";
              break;
            case "Gender":
              temporaryCard.description =
                getLabelGender(patient?.gender) || "-";
              break;
            case "Ethnicity":
              temporaryCard.description =
                getLabelEthnicity(patient?.ethnicity) || "-";
              break;
            case "Allergies":
              temporaryCard.description =
                patient?.allergies && patient.allergies.length > 0
                  ? patient.allergies
                      .map((a) => `${a.preferredTerm} - ${a.conceptId}`)
                      .join("\n")
                  : "-";
              break;
            case "Diagnosis":
              temporaryCard.description =
                patient?.diagnoses && patient.diagnoses.length > 0
                  ? patient.diagnoses
                      .map((d) => `${d.preferredTerm} - ${d.conceptId}`)
                      .join(", ")
                  : "-";
              break;
            default: {
              // EN-5067: re-hydrate a saved custom-field card from the fresh query. Drop it only
              // if it no longer exists (field removed in Retool or no longer shown for the patient).
              const customCard = customCardsByName.get(card.name);
              if (!customCard) return;
              temporaryCard.description = customCard.description;
              temporaryCard.customFieldMeta = customCard.customFieldMeta;
              break;
            }
          }

          temporaryPatientCards.push(temporaryCard);
        });
        // Add in any default cards names that are not in the parsed list
        const listOfCardNames = defaultCards.map((item) => {
          return item.name;
        });
        const listOfTempCardNames = temporaryPatientCards.map((item) => {
          return item.name;
        });
        // Get the names that are in the default list but not the parsed list
        const difference = listOfCardNames.filter(
          (x) => !listOfTempCardNames.includes(x)
        );

        difference.forEach((name) => {
          // Get the object from the default list by name
          const cardObject = getObjectByName(name);
          if (cardObject) {
            // EN-5067: a card present in the catalog but missing from the saved order (a newly
            // shipped built-in, or a newly-available custom field) defaults to inactive/off. The
            // HCP opts it in via "manage fields"; it stays fully movable/toggleable once shown.
            cardObject.active = false;
            const object = temporaryPatientCards.find(
              (a) => a.order === cardObject.order
            );
            if (!object) {
              temporaryPatientCards.push(cardObject);
            } else {
              // If object order conflicts, then add it to the end (highest order)
              const orders = temporaryPatientCards.map((object) => {
                return object.order;
              });
              cardObject.order = Math.max(...orders) + 1;
              temporaryPatientCards.push(cardObject);
            }
          }
        });
        // EN-5060: saved card orders may predate removing the condition card, so
        // strip it from stored layouts.
        setPatientCards(
          temporaryPatientCards.filter((card) => card.name !== "Condition")
        );
      } else {
        setDefaultCardsOrder();
      }
    } else {
      setDefaultCardsOrder();
    }
  }, [
    patient,
    patientMonitoringInfo,
    userPreferences,
    dropdownItems,
    isPreferencesFetching,
    // EN-5067: rebuild the cards once the patient's custom-field values load/change.
    patientCustomFields,
  ]);

  const saveLastPatiendCardsConfig = () => {
    setLastPatientCards([...patientCards]);
  };

  const handleOnEditClick = () => {
    saveLastPatiendCardsConfig();

    // Seed the ward multi-select from the patient's current set (primary first, falling back to
    // hospitalId) and remember it so we can detect a real change on Save.
    const currentWardIds = resolvePatientWardIds(
      patientWards?.wardIds,
      patient.hospitalId
    );
    originalWardIdsRef.current = currentWardIds;
    setSelectedWardIds(currentWardIds);

    // EN-5067: snapshot the current custom-field answers so we can diff on Save, and clear any
    // pending edits from a previous session.
    const originalAnswers: Record<string, ICustomFieldAnswer> = {};
    patientCustomFields.forEach((field) => {
      originalAnswers[field.customFieldDefinitionId] = {
        value: field.value ?? undefined,
        selectedOptions: field.selectedOptions ?? [],
      };
    });
    originalCustomFieldAnswersRef.current = originalAnswers;
    setCustomFieldEdits({});

    setIsEditing(true);
    onEditingClick(true);
  };

  const updateCardsOrderByPosition = () => {
    const newOrderedCards: PatientCard[] = [];

    const cards: NodeListOf<HTMLDivElement> =
      document.querySelectorAll("[dragging]");

    patientCards.forEach((patientCard) => {
      cards.forEach((htmlCard, index) => {
        if (htmlCard.getAttribute("name") === patientCard.name) {
          const newPatientCard = { ...patientCard };

          newPatientCard.order = index + 1;

          newOrderedCards.push(newPatientCard);
        }
      });
    });

    return newOrderedCards;
  };

  const handleOnSave = async (
    onlyCardsOrder: boolean,
    sendEnrolmentEmail: boolean,
    newOrder: PatientCard[]
  ) => {
    const patientOperations: IPatchOperation[] = [];
    const cardsOrderToSave: PatientCard[] = [];

    if (!onlyCardsOrder) {
      propsChanged.forEach((prop) => {
        let newValue: IOperationValue;

        // EN-5067: custom fields are NOT part of the JSON-patch details update — they're saved via
        // PUT /CustomFields below. Skip before the cardsCofigs lookup (they have no entry).
        if (prop.propName.startsWith(CUSTOM_CARD_PREFIX)) {
          return;
        }

        const propIndex = cardsCofigs.findIndex(
          (x) => x.name === prop.propName
        );

        if (prop.propName === "Country") {
          const newCountry = dropdownItems.countryItems?.find(
            (x) => x.itemName === prop.newValue
          );

          const oldCountry = dropdownItems.countryItems?.find(
            (x) => x.itemName === prop.oldValue
          );

          newValue = {
            newValue: newCountry?.itemId.toString(),
            oldValue: oldCountry?.itemId.toString(),
          };
        } else if (prop.propName === "Gender") {
          const newSex = sexOptions.find(
            (x) => t(x.labelKey) === prop.newValue
          );
          const oldSex = sexOptions.find(
            (x) => t(x.labelKey) === prop.oldValue
          );

          newValue = {
            newValue: newSex?.value,
            oldValue: oldSex?.value,
          };
        } else if (prop.propName === "Ethnicity") {
          const newEthnicity = ethnicityOptions.find(
            (x) => t(x.labelKey) === prop.newValue
          );
          const oldEthnicity = ethnicityOptions.find(
            (x) => t(x.labelKey) === prop.oldValue
          );

          newValue = {
            newValue: newEthnicity?.value,
            oldValue: oldEthnicity?.value,
          };
        } else if (
          prop.propName === "Allergies" ||
          prop.propName === "Diagnosis"
        ) {
          // Kept as strings — SNOMED conceptIds can exceed
          // Number.MAX_SAFE_INTEGER and would be corrupted by Number().
          const toConceptIds = (raw?: string) => {
            if (!raw || raw === "-") return [] as string[];
            return raw
              .split("\n")
              .map((label) => {
                const sep = label.lastIndexOf(" - ");
                return sep === -1 ? "" : label.slice(sep + 3);
              })
              .filter((id) => /^\d+$/.test(id));
          };

          newValue = {
            newValue: JSON.stringify(toConceptIds(prop.newValue ?? undefined)),
            oldValue: JSON.stringify(toConceptIds(prop.oldValue)),
          };
        } else if (prop.propName === "DOB") {
          newValue = {
            newValue: dayjs(prop.newValue).format("YYYY/MM/DD"),
            oldValue: dayjs(prop.oldValue).format("YYYY/MM/DD"),
          };
        } else if (prop.propName === "Ward") {
          // Wards are NOT part of the JSON-patch details update — they're a multi-value set saved
          // via PUT /Wards below. Skip building a /hospital_id op (that would collapse the set).
          return;
        } else {
          newValue = {
            newValue: prop.newValue?.toString(),
            oldValue: prop.oldValue.toString(),
            sendEnrolmentEmail:
              prop.propName === "Email" ? sendEnrolmentEmail : false,
          };
        }

        patientOperations.push({
          op: "add",
          path: cardsCofigs[propIndex].path ?? "",
          value: newValue,
        });
      });
    }

    newOrder.forEach((card) => {
      const newCard = { ...card };

      // The backend validates the cards-order blob against a strict { name, order, active } shape
      // and silently drops the ENTIRE saved order if any entry carries an extra property. Custom
      // cards ride a `customFieldMeta` object (and every card a `description`) purely for rendering,
      // so strip both before persisting. The `custom:` name prefix is enough for the reconcile pass
      // to re-hydrate the card's metadata from the CustomFields query on the next load.
      delete newCard.description;
      delete newCard.customFieldMeta;

      cardsOrderToSave.push(newCard);
    });

    const cardsOrderJson = JSON.stringify(cardsOrderToSave);

    // EN-4960: the ward set is saved separately via PUT /Wards (the server derives the primary and
    // keeps hospital_id in sync). Do it before the details patch so a same-hospital validation
    // failure aborts cleanly without half-applying the edit.
    const wardsChanged =
      !onlyCardsOrder &&
      selectedWardIds.length > 0 &&
      !sameWardSet(selectedWardIds, originalWardIdsRef.current);

    if (wardsChanged) {
      try {
        await setPatientWards({
          patientId: patient.id,
          wardIds: selectedWardIds,
        });
      } catch (error) {
        // The server explains a refused assignment (e.g. wards from different hospitals).
        showToast(
          serverErrorMessage(error) || t("patientPage_lateralMenu_saveFailed"),
          "error"
        );
        closeModal();
        return;
      }
      await refetchPatientWards();
    }

    // EN-5067: save changed custom-field values via PUT /CustomFields (partial upsert). Like the
    // ward save, done before the details patch so a validation failure aborts cleanly.
    if (!onlyCardsOrder) {
      const changedCustomFieldIds = Object.keys(customFieldEdits).filter(
        (id) =>
          !sameAnswer(
            customFieldEdits[id],
            originalCustomFieldAnswersRef.current[id] ?? {}
          )
      );

      if (changedCustomFieldIds.length > 0) {
        const changedItems: ICustomFieldFormItem[] = [];
        const changedAnswers: Record<string, ICustomFieldAnswer> = {};
        changedCustomFieldIds.forEach((id) => {
          const field = patientCustomFields.find(
            (item) => item.customFieldDefinitionId === id
          );
          if (!field) return;
          changedItems.push({
            customFieldDefinitionId: id,
            label: field.label,
            key: field.key,
            type: field.type,
            options: field.options,
            validation: field.validation,
            isRequired: field.isRequired,
            displayOrder: field.displayOrder,
          });
          changedAnswers[id] = customFieldEdits[id];
        });

        // Authoritative client guard (the shared hasError flag can miss a field). Abort on error.
        const cfErrors = validateCustomFields(changedItems, changedAnswers);
        if (Object.keys(cfErrors).length > 0) {
          showToast(Object.values(cfErrors)[0], "error");
          closeModal();
          return;
        }

        const customFieldsPayload = buildCustomFieldEditPayload(
          changedItems,
          changedAnswers
        );

        try {
          await setPatientCustomFields({
            patientId: patient.id,
            customFields: customFieldsPayload,
          });
        } catch (error) {
          // Messages are already prefixed with the field label by the server.
          showToast(
            serverErrorMessage(error) ||
              t("patientPage_lateralMenu_customFieldsFailed"),
            "error"
          );
          await refetchPatientCustomFields();
          closeModal();
          return;
        }

        await refetchPatientCustomFields();
      }
    }

    const result = await patientService.updatePatientDetailsAndCardsOrder(
      patient.id,
      patientOperations,
      cardsOrderJson
    );

    if (result.status >= 200 && result.status < 300) {
      setPropsChanged([]);
      setCustomFieldEdits({});
      await refetch();
      await refetchPatientInfo();

      showToast(t("patientPage_lateralMenu_saved"), "success");
    } else {
      // Surface the backend's field-level error when present (e.g. the new
      // "/ppsn is required for this hospital..." rejection). Falls back to the
      // generic copy for transport errors or unexpected response shapes.
      const apiErrorMessage = (result.data as IApiErrorResponse | undefined)
        ?.errors?.[0]?.message;
      showToast(
        apiErrorMessage || t("patientPage_lateralMenu_saveFailed"),
        "error"
      );
    }

    setIsEditing(false);
    onEditingClick(false);
    closeModal();
  };

  const resetLastCardConfig = () => {
    setPatientCards(lastPatientCards);
  };

  const handleOnCancelEditionClick = () => {
    resetLastCardConfig();
    setPropsChanged([]);
    setCustomFieldEdits({});
    setIsEditing(false);
    onEditingClick(false);
  };

  const handleModalOnClose = () => {
    resetLastCardConfig();
    setPropsChanged([]);
    closeModal();
  };

  const handleOnSaveEditionClick = () => {
    const newOrder = updateCardsOrderByPosition();

    openModal(
      <ConfirmChangesModal
        propsChanged={propsChanged}
        onClose={handleModalOnClose}
        onSave={(onlyCardsOrder: boolean, sendEnrolmentEmail: boolean) =>
          handleOnSave(onlyCardsOrder, sendEnrolmentEmail, newOrder)
        }
      />,
      {
        width: "611px",
        height: "auto",
        maxHeight: "90vh",
        showCloseButton: true,
      }
    );
  };

  const getNextCard = (currentCardPosY: number) => {
    let result: HTMLDivElement | undefined;

    const cards: NodeListOf<HTMLDivElement> =
      document.querySelectorAll("[dragging='false']");

    cards.forEach((card) => {
      const isNotVisibleCard = card.className.includes("isVisible-false");
      if (isNotVisibleCard) return;

      const cardBox = card.getBoundingClientRect();
      const cardBoxCenterY = cardBox.y + cardBox.height / 2;

      const currentCardIsOverOrInsideThisCard =
        currentCardPosY >= cardBoxCenterY;

      if (currentCardIsOverOrInsideThisCard) result = card;
    });

    return result;
  };

  const handleOnDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault(); // Avoid system not-alowed cursor

    const nextCard = getNextCard(event.clientY);

    const dragging: HTMLDivElement | null =
      document.querySelector("[dragging='true']");

    if (dragging) {
      if (nextCard) {
        nextCard.insertAdjacentElement("afterend", dragging);
      } else {
        menuRef.current?.prepend(dragging);
      }
    }
  };

  const cardsOrderComparer = (a: PatientCard, b: PatientCard) => {
    if (a.active !== b.active) {
      if (a.active) return -1;

      if (b.active) return 1;
    }

    if (a.order === b.order) return 0;

    return a.order > b.order ? 1 : -1;
  };

  const handleIsEditable = (card: PatientCard) => {
    const patientCard = patientCards.find((x) => x.name === card.name);

    // EN-5067: custom-field cards have no cardsCofigs entry; they're editable while editing and
    // active, and saved via PUT /CustomFields (not the JSON-patch flow).
    if (card.customFieldMeta) {
      return isEditing && !!patientCard?.active;
    }

    const prop = cardsCofigs.find((x) => x.name === card.name);

    if (!isEditing || !prop?.isEditable || !patientCard?.active) return false;

    // Only one ward is available to this user → there's nothing to change, so keep the Ward card
    // read-only. (Computed locally — never mutate the shared cardsCofigs.)
    if (
      card.name === "Ward" &&
      (dropdownItems?.hospitalItems?.length ?? 0) <= 1
    ) {
      return false;
    }

    return true;
  };

  const handleOnCardButtonClick = (card: PatientCard) => {
    const newPatientCard: PatientCard = { ...card, active: !card.active };

    // Deactivating a card discards any pending value edit for it. The card reverts to its
    // stored value on toggle, so a stale change (e.g. a cleared required field) must not be
    // carried into the save — the backend would reject it and the Confirm Changes modal
    // would surface a change the user can no longer see.
    if (!newPatientCard.active) {
      setPropsChanged((previousProps) =>
        previousProps.filter((prop) => prop.propName !== card.name)
      );
    }

    const activeCards: PatientCard[] = [];
    const inactiveCards: PatientCard[] = [];

    patientCards.forEach((patientCard) => {
      const isIgnoringCardToChange = patientCard.order !== card.order;

      if (isIgnoringCardToChange) {
        if (patientCard.active) activeCards.push(patientCard);
        else inactiveCards.push(patientCard);
      }
    });

    const newCards = [...activeCards, newPatientCard, ...inactiveCards];

    const newOrderedCards = newCards.map((card, idx) => {
      const order = idx + 1;
      return { ...card, order };
    });

    setPatientCards(newOrderedCards);
  };

  const handleOnPropEdit = (
    propName: string,
    newValue: string | null,
    oldValue: string
  ) => {
    if (oldValue.toString() === newValue?.trim()) return;

    const propertyIndex = propsChanged.findIndex(
      (x) => x.propName === propName
    );

    const temporaryPropsObj = propsChanged;

    if (newValue !== null) {
      if (propertyIndex !== -1) {
        temporaryPropsObj[propertyIndex] = {
          propName,
          newValue,
          oldValue,
        };
      } else {
        temporaryPropsObj.push({
          propName,
          newValue,
          oldValue,
        });
      }
    } else {
      temporaryPropsObj.splice(propertyIndex, 1);
    }

    setPropsChanged(temporaryPropsObj);
  };

  const handleDropdownItems = (card: PatientCard) => {
    const options: IOption[] = [];

    if (card.name === "Country" && dropdownItems?.countryItems) {
      dropdownItems?.countryItems?.forEach((item, index) => {
        options.push({
          label: item.itemName,
          value: item.itemId.toString(),
          key: `${item.itemName}-${item.itemId}-${index}`,
        });
      });
    }

    if (card.name === "Ward" && dropdownItems?.hospitalItems) {
      dropdownItems?.hospitalItems?.forEach((item, index) => {
        options.push({
          label: item.itemName,
          value: item.itemId.toString(),
          key: `${item.itemName}-${item.itemId}-${index}`,
        });
      });
    }

    if (card.name === "Gender") {
      sexOptions.forEach((item, index) => {
        options.push({
          label: t(item.labelKey),
          value: item.value,
          key: `${item.value}-${index}`,
        });
      });
    }

    if (card.name === "Ethnicity") {
      ethnicityOptions.forEach((item, index) => {
        options.push({
          label: t(item.labelKey),
          value: item.value,
          key: `${item.value}-${index}`,
        });
      });
    }

    return options;
  };

  const childComponents = useMemo(() => {
    if (!patientCards) return [];

    const visibleCards = patientCards.filter((card) =>
      isOnboardingCardVisible(card.name)
    );

    return visibleCards.sort(cardsOrderComparer).map((card) => {
      return (
        <PatientMenuCard
          key={uuidv4()}
          card={card}
          isEditing={isEditing}
          isEditable={handleIsEditable(card)}
          isRequired={isCardRequired(card.name)}
          onDragOver={handleOnDragOver}
          onCardButtonClick={() => handleOnCardButtonClick(card)}
          onPropEdit={handleOnPropEdit}
          dropdownItems={handleDropdownItems(card)}
          onError={(error) => {
            setHasError(error);
          }}
          isWardCard={card.name === "Ward"}
          wardOptions={card.name === "Ward" ? wardOptions : undefined}
          // Seed only — the card self-controls its selection after mount. Sourced from the ref
          // (set when Edit starts) so the card list doesn't re-render/remount on every ward pick.
          wardValue={
            card.name === "Ward"
              ? originalWardIdsRef.current.map((id) => id.toString())
              : undefined
          }
          onWardChange={card.name === "Ward" ? handleWardChange : undefined}
          // EN-5067: seed only (from the snapshot taken when Edit started); the editor
          // self-controls after mount and reports changes via onCustomFieldChange.
          customFieldValue={
            card.customFieldMeta
              ? originalCustomFieldAnswersRef.current[
                  card.customFieldMeta.definitionId
                ] ?? {}
              : undefined
          }
          onCustomFieldChange={
            card.customFieldMeta ? handleCustomFieldChange : undefined
          }
        />
      );
    });
  }, [
    patientCards,
    isEditing,
    dropdownItems,
    enabledOnboardingFields,
    wardOptions,
  ]);

  return (
    <PatientLateralMenu isLateralPatientMenuOpenOnDesktop={open}>
      {!isEditing && (
        <EditButtonContainer>
          {/* EN-5268: Edit seeds the ward set and PUT /Wards replaces it, so wait for the real wards
              (and stay disabled if they failed) instead of editing from the primary-only fallback. */}
          <EditButton
            onClick={handleOnEditClick}
            disabled={!isPatientWardsSuccess}
          >
            {t("patientPage_lateralMenu_edit")}
          </EditButton>
        </EditButtonContainer>
      )}

      <PatientLateralMenuCardContainer
        ref={menuRef}
        isLateralPatientMenuOpenOnDesktop={open}
      >
        {childComponents}

        <div style={{ height: isEditing ? "95px" : "80px", width: "100%" }} />
      </PatientLateralMenuCardContainer>

      {isEditing && (
        <EditControlsContainer>
          <EditControlsButton
            onClick={handleOnSaveEditionClick}
            primary
            disabled={hasError}
          >
            {t("common_save")}
          </EditControlsButton>

          <EditControlsButton onClick={handleOnCancelEditionClick}>
            {t("common_cancel")}
          </EditControlsButton>
        </EditControlsContainer>
      )}

      {!isEditing && (
        <ExpandButton isExpanded={open} onClick={onExpandClick}>
          <ArrowRight />
        </ExpandButton>
      )}
    </PatientLateralMenu>
  );
}
